import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Copy, ExternalLink, Trash2, Power, PowerOff, AlertCircle, ChevronDown, ChevronUp, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";

interface EmailForm {
  id: string;
  name: string;
  description: string | null;
  google_form_id: string;
  google_form_url: string;
  fields_mapping: Record<string, string>;
  is_active: boolean;
  created_at: string;
  max_submissions: number | null;
  auto_deactivate: boolean;
}

interface FormField {
  id: string;
  title: string;
  type: string;
}

export default function EmailForms() {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [emailField, setEmailField] = useState("");
  const [nameField, setNameField] = useState("");
  const [phoneField, setPhoneField] = useState("");
  const [companyField, setCompanyField] = useState("");
  const [cityField, setCityField] = useState("");
  const [stateField, setStateField] = useState("");
  const [hasLimit, setHasLimit] = useState(false);
  const [maxSubmissions, setMaxSubmissions] = useState<number>(100);
  const [autoDeactivate, setAutoDeactivate] = useState(true);
  const [openCodes, setOpenCodes] = useState<Record<string, boolean>>({});
  const [availableFields, setAvailableFields] = useState<FormField[]>([]);
  const [isFetchingFields, setIsFetchingFields] = useState(false);
  const queryClient = useQueryClient();

  const { data: forms = [], isLoading } = useQuery({
    queryKey: ["email-forms"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_forms")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as EmailForm[];
    },
  });

  // Fetch submission counts for each form
  const { data: submissionCounts } = useQuery({
    queryKey: ["submission-counts", forms?.map(f => f.id)],
    queryFn: async () => {
      if (!forms) return {};
      
      const counts: Record<string, number> = {};
      
      for (const form of forms) {
        const { count, error } = await supabase
          .from('google_form_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('email_form_id', form.id);
        
        if (!error && count !== null) {
          counts[form.id] = count;
        }
      }
      
      return counts;
    },
    enabled: !!forms && forms.length > 0,
  });

  const createFormMutation = useMutation({
    mutationFn: async (newForm: {
      name: string;
      description: string;
      google_form_url: string;
      google_form_id: string;
      fields_mapping: Record<string, string>;
      max_submissions: number | null;
      auto_deactivate: boolean;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { data, error } = await supabase
        .from("email_forms")
        .insert({
          ...newForm,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-forms"] });
      toast.success("Formulário cadastrado com sucesso!");
      setIsDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error("Erro ao cadastrar formulário: " + error.message);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("email_forms")
        .update({ is_active })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-forms"] });
      queryClient.invalidateQueries({ queryKey: ["submission-counts"] });
      toast.success("Status atualizado!");
    },
  });

  const deleteFormMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("email_forms").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-forms"] });
      queryClient.invalidateQueries({ queryKey: ["submission-counts"] });
      toast.success("Formulário excluído!");
    },
  });

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormUrl("");
    setEmailField("");
    setNameField("");
    setPhoneField("");
    setCompanyField("");
    setCityField("");
    setStateField("");
    setHasLimit(false);
    setMaxSubmissions(100);
    setAutoDeactivate(true);
    setAvailableFields([]);
  };

  const fetchFormFields = async () => {
    if (!formUrl) {
      toast.error("Por favor, insira a URL do formulário primeiro");
      return;
    }

    const formId = extractFormId(formUrl);
    if (!formId) {
      toast.error("URL do formulário inválida");
      return;
    }

    setIsFetchingFields(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-google-form-fields', {
        body: { formUrl }
      });

      if (error) {
        const errorMsg = (error as any)?.message || 'Falha ao chamar a função';
        throw new Error(errorMsg);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao buscar campos do formulário');
      }

      if (data.fields) {
        setAvailableFields(data.fields);
        if (!formName && data.formTitle) {
          setFormName(data.formTitle);
        }
        toast.success(`${data.fields.length} campos encontrados!`);
      }
    } catch (error: any) {
      console.error('Error fetching form fields:', error);
      toast.error(error.message || "Erro ao buscar campos do formulário");
    } finally {
      setIsFetchingFields(false);
    }
  };

  const extractFormId = (url: string) => {
    const match = url.match(/\/forms\/d\/(?:e\/)?([a-zA-Z0-9_-]+)/);
    return match ? match[1] : "";
  };

  const handleSubmit = () => {
    const formId = extractFormId(formUrl);
    if (!formId) {
      toast.error("URL do formulário inválida");
      return;
    }

    if (!emailField || !nameField) {
      toast.error("Email e Nome são campos obrigatórios");
      return;
    }

    createFormMutation.mutate({
      name: formName,
      description: formDescription,
      google_form_url: formUrl,
      google_form_id: formId,
      fields_mapping: {
        email: emailField,
        name: nameField,
        phone: phoneField,
        company: companyField,
        city: cityField,
        state: stateField,
      },
      max_submissions: hasLimit ? maxSubmissions : null,
      auto_deactivate: hasLimit ? autoDeactivate : true,
    });
  };

  const generateAppsScript = (form: EmailForm) => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    return `// ====================================
// Google Apps Script - Integração com Webhook
// ====================================
// INSTRUÇÕES:
// 1. Cole este código completo no Apps Script
// 2. Salve o projeto (Ctrl+S ou ícone de disquete)
// 3. Execute a função "createTrigger" uma vez para configurar
// 4. Autorize as permissões quando solicitado
// 5. Teste enviando uma resposta no formulário
// ====================================

const WEBHOOK_URL = 'https://${projectId}.supabase.co/functions/v1/google-form-webhook';
const FORM_ID = '${form.id}'; // UUID interno do formulário (NÃO SUBSTITUA!)
const GOOGLE_FORM_ID = '${form.google_form_id}'; // ID do Google Forms

// Esta função será executada automaticamente ao enviar o formulário
function onFormSubmit(e) {
  const responses = {};
  const itemResponses = e.response.getItemResponses();
  
  itemResponses.forEach(itemResponse => {
    const question = itemResponse.getItem().getTitle();
    const answer = itemResponse.getResponse();
    responses[question] = answer;
  });
  
  const payload = {
    email_form_id: FORM_ID, // UUID interno
    google_form_id: GOOGLE_FORM_ID, // ID do Google (redundância)
    response_id: e.response.getId(),
    responses: responses,
    timestamp: new Date().toISOString()
  };
  
  const options = {
    method: 'post',
    contentType: 'application/json',
    payload: JSON.stringify(payload),
    muteHttpExceptions: true
  };
  
  try {
    const response = UrlFetchApp.fetch(WEBHOOK_URL, options);
    Logger.log('✅ Dados enviados com sucesso! Status: ' + response.getResponseCode());
    Logger.log('Resposta: ' + response.getContentText());
  } catch (error) {
    Logger.log('❌ Erro ao enviar dados: ' + error);
  }
}

// Execute esta função UMA VEZ para criar o acionador automaticamente
function createTrigger() {
  // Remove acionadores antigos (se houver)
  const triggers = ScriptApp.getProjectTriggers();
  triggers.forEach(trigger => {
    if (trigger.getHandlerFunction() === 'onFormSubmit') {
      ScriptApp.deleteTrigger(trigger);
    }
  });
  
  // Cria novo acionador
  const form = FormApp.getActiveForm();
  ScriptApp.newTrigger('onFormSubmit')
    .forForm(form)
    .onFormSubmit()
    .create();
  
  Logger.log('✅ Acionador criado com sucesso!');
}

// Função para testar o webhook manualmente (sem enviar formulário)
function testPing() {
  const testPayload = {
    email_form_id: FORM_ID, // UUID interno
    google_form_id: GOOGLE_FORM_ID, // ID do Google (redundância)
    response_id: 'test_' + new Date().getTime(),
    responses: {
      'Email': 'teste@exemplo.com',
      'Nome': 'Teste Manual',
      'Telefone': '11999999999',
      'Empresa': 'Teste Ltda'
    },
    timestamp: new Date().toISOString()
  };
  
  const options = {
    method: 'post',
    contentType: 'application/json',
    payload: JSON.stringify(testPayload),
    muteHttpExceptions: true
  };
  
  try {
    const response = UrlFetchApp.fetch(WEBHOOK_URL, options);
    Logger.log('✅ Teste enviado! Status: ' + response.getResponseCode());
    Logger.log('Resposta: ' + response.getContentText());
  } catch (error) {
    Logger.log('❌ Erro no teste: ' + error);
  }
}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Código copiado para a área de transferência!");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="space-y-4 mb-8">
        <Button 
          variant="outline" 
          onClick={() => navigate("/")}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao Menu Principal
        </Button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Formulários Google</h1>
            <p className="text-muted-foreground mt-2">
              Gerencie a integração com Google Forms para captura de leads
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Novo Formulário
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cadastrar Novo Formulário</DialogTitle>
              <DialogDescription>
                Configure a integração com um formulário do Google Forms
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nome do Formulário</Label>
                <Input
                  id="name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ex: Formulário de Interesse"
                />
              </div>
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Descreva o propósito deste formulário"
                />
              </div>
              <div>
                <Label htmlFor="url">URL do Google Form</Label>
                <div className="flex gap-2">
                  <Input
                    id="url"
                    value={formUrl}
                    onChange={(e) => {
                      setFormUrl(e.target.value);
                      setAvailableFields([]);
                    }}
                    placeholder="https://docs.google.com/forms/d/..."
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={fetchFormFields}
                    disabled={!formUrl || !extractFormId(formUrl) || isFetchingFields}
                  >
                    {isFetchingFields ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Buscando...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Buscar Campos
                      </>
                    )}
                  </Button>
                </div>
                {formUrl && extractFormId(formUrl) && (
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      ID detectado: {extractFormId(formUrl)}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(extractFormId(formUrl));
                        toast.success("ID copiado!");
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                {formUrl && !extractFormId(formUrl) && (
                  <p className="text-xs text-destructive mt-1">
                    ⚠️ URL inválida. Use o formato: https://docs.google.com/forms/d/...
                  </p>
                )}
                {availableFields.length > 0 && (
                  <Alert className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Campos encontrados!</AlertTitle>
                    <AlertDescription>
                      {availableFields.length} campos disponíveis. Use os dropdowns abaixo para mapear.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Mapeamento de Campos</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {availableFields.length > 0 
                    ? "Selecione o campo correspondente para cada informação" 
                    : "Digite o título de cada pergunta no seu Google Form ou use o botão 'Buscar Campos' acima"}
                </p>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="email">Campo de Email *</Label>
                    {availableFields.length > 0 ? (
                      <Select value={emailField} onValueChange={setEmailField}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o campo de email" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableFields.map((field) => (
                            <SelectItem key={field.id} value={field.title}>
                              {field.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="email"
                        value={emailField}
                        onChange={(e) => setEmailField(e.target.value)}
                        placeholder="Ex: Email"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="name">Campo de Nome *</Label>
                    {availableFields.length > 0 ? (
                      <Select value={nameField} onValueChange={setNameField}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o campo de nome" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableFields.map((field) => (
                            <SelectItem key={field.id} value={field.title}>
                              {field.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="name"
                        value={nameField}
                        onChange={(e) => setNameField(e.target.value)}
                        placeholder="Ex: Nome completo"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="phone">Campo de Telefone</Label>
                    {availableFields.length > 0 ? (
                      <Select value={phoneField} onValueChange={setPhoneField}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o campo de telefone (opcional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Nenhum</SelectItem>
                          {availableFields.map((field) => (
                            <SelectItem key={field.id} value={field.title}>
                              {field.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="phone"
                        value={phoneField}
                        onChange={(e) => setPhoneField(e.target.value)}
                        placeholder="Ex: Telefone"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="company">Campo de Empresa</Label>
                    {availableFields.length > 0 ? (
                      <Select value={companyField} onValueChange={setCompanyField}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o campo de empresa (opcional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Nenhum</SelectItem>
                          {availableFields.map((field) => (
                            <SelectItem key={field.id} value={field.title}>
                              {field.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="company"
                        value={companyField}
                        onChange={(e) => setCompanyField(e.target.value)}
                        placeholder="Ex: Empresa"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="city">Campo de Cidade</Label>
                    {availableFields.length > 0 ? (
                      <Select value={cityField} onValueChange={setCityField}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o campo de cidade (opcional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Nenhum</SelectItem>
                          {availableFields.map((field) => (
                            <SelectItem key={field.id} value={field.title}>
                              {field.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="city"
                        value={cityField}
                        onChange={(e) => setCityField(e.target.value)}
                        placeholder="Ex: Cidade"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="state">Campo de Estado</Label>
                    {availableFields.length > 0 ? (
                      <Select value={stateField} onValueChange={setStateField}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o campo de estado (opcional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Nenhum</SelectItem>
                          {availableFields.map((field) => (
                            <SelectItem key={field.id} value={field.title}>
                              {field.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="state"
                        value={stateField}
                        onChange={(e) => setStateField(e.target.value)}
                        placeholder="Ex: Estado"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hasLimit"
                    checked={hasLimit}
                    onCheckedChange={(checked) => setHasLimit(checked as boolean)}
                  />
                  <label
                    htmlFor="hasLimit"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Limitar número de inscrições
                  </label>
                </div>

                {hasLimit && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="maxSubmissions">Número máximo de vagas</Label>
                      <Input
                        id="maxSubmissions"
                        type="number"
                        min="1"
                        value={maxSubmissions}
                        onChange={(e) => setMaxSubmissions(parseInt(e.target.value) || 100)}
                        placeholder="Ex: 100"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="autoDeactivate"
                        checked={autoDeactivate}
                        onCheckedChange={(checked) => setAutoDeactivate(checked as boolean)}
                      />
                      <label
                        htmlFor="autoDeactivate"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Desativar automaticamente ao atingir limite
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>Cadastrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid gap-6">
        {forms.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">Nenhum formulário cadastrado ainda</p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Primeiro Formulário
              </Button>
            </CardContent>
          </Card>
        ) : (
          forms.map((form) => (
            <Card key={form.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {form.name}
                      {(() => {
                        const count = submissionCounts?.[form.id] || 0;
                        const max = form.max_submissions;
                        const isLimited = max !== null;
                        const isFull = isLimited && count >= max;
                        const percentage = isLimited ? (count / max) * 100 : 0;
                        
                        let variant: "default" | "secondary" | "destructive" = "default";
                        let label = "● Ativo";
                        
                        if (isFull) {
                          variant = "destructive";
                          label = "⚫ LOTADO";
                        } else if (!form.is_active) {
                          variant = "secondary";
                          label = "○ Inativo";
                        } else if (isLimited && percentage >= 90) {
                          variant = "destructive";
                          label = "🔴 Ativo (Quase lotado)";
                        } else if (isLimited && percentage >= 70) {
                          label = "🟡 Ativo (Enchendo)";
                        }
                        
                        return <Badge variant={variant}>{label}</Badge>;
                      })()}
                    </CardTitle>
                    {form.description && (
                      <CardDescription className="mt-2">{form.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        toggleActiveMutation.mutate({
                          id: form.id,
                          is_active: !form.is_active,
                        })
                      }
                    >
                      {form.is_active ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => deleteFormMutation.mutate(form.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Capacity Progress Bar */}
                {form.max_submissions !== null && (
                  <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">📊 Controle de Vagas</span>
                      <span className="text-muted-foreground">
                        {submissionCounts?.[form.id] || 0}/{form.max_submissions}
                      </span>
                    </div>
                    <Progress 
                      value={((submissionCounts?.[form.id] || 0) / form.max_submissions) * 100} 
                      className="h-3"
                    />
                    {(() => {
                      const count = submissionCounts?.[form.id] || 0;
                      const remaining = form.max_submissions - count;
                      const percentage = (count / form.max_submissions) * 100;
                      
                      if (remaining <= 0) {
                        return <p className="text-sm text-destructive font-medium">🚫 Limite atingido</p>;
                      } else if (percentage >= 90) {
                        return <p className="text-sm text-destructive font-medium">🔴 ATENÇÃO: Apenas {remaining} vagas!</p>;
                      } else if (percentage >= 70) {
                        return <p className="text-sm text-orange-600 font-medium">🟡 {remaining} vagas restantes</p>;
                      } else {
                        return <p className="text-sm text-muted-foreground">🟢 {remaining} vagas disponíveis</p>;
                      }
                    })()}
                    <p className="text-xs text-muted-foreground">
                      Desativação automática: {form.auto_deactivate ? "Sim" : "Não"}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <a
                    href={form.google_form_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    Abrir formulário no Google
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="border rounded-lg p-4 bg-muted/50">
                  <Collapsible
                    open={openCodes[form.id] || false}
                    onOpenChange={() => setOpenCodes(prev => ({ ...prev, [form.id]: !prev[form.id] }))}
                  >
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold">Código para Google Apps Script</Label>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(generateAppsScript(form))}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copiar
                        </Button>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm">
                            {openCodes[form.id] ? (
                              <>
                                <ChevronUp className="h-4 w-4 mr-2" />
                                Esconder Código
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-4 w-4 mr-2" />
                                Mostrar Código
                              </>
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                    
                    <CollapsibleContent className="mt-4 space-y-3">
                      <pre className="text-xs overflow-x-auto p-3 bg-background rounded border">
                        <code>{generateAppsScript(form)}</code>
                      </pre>
                      
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Como configurar (MÉTODO SIMPLIFICADO)</AlertTitle>
                        <AlertDescription className="space-y-2 mt-2">
                          <div>
                            <strong>1. Abra o Google Form</strong>
                            <br />
                            <span className="text-xs">Clique em "Abrir formulário no Google" acima</span>
                          </div>
                          <div>
                            <strong>2. Acesse Apps Script</strong>
                            <br />
                            <span className="text-xs">Menu: Extensões → Apps Script</span>
                          </div>
                          <div>
                            <strong>3. Cole o código completo</strong>
                            <br />
                            <span className="text-xs">Substitua todo o código existente (use o botão Copiar acima)</span>
                          </div>
                          <div>
                            <strong>4. Salve o projeto</strong>
                            <br />
                            <span className="text-xs">Clique no ícone de disquete ou Ctrl+S</span>
                          </div>
                          <div className="bg-primary/10 p-2 rounded border border-primary/20">
                            <strong>5. Execute "createTrigger" (AUTOMÁTICO!) ⚡</strong>
                            <br />
                            <span className="text-xs">
                              • No topo, selecione a função <strong>createTrigger</strong> no dropdown
                              <br />
                              • Clique em "Executar" (▶️)
                              <br />
                              • Autorize as permissões quando solicitado
                              <br />
                              • Pronto! O acionador foi criado automaticamente
                            </span>
                          </div>
                          <div>
                            <strong>6. Teste (OPCIONAL)</strong>
                            <br />
                            <span className="text-xs">
                              Execute a função <strong>testPing</strong> para testar o webhook sem enviar o formulário
                              <br />
                              Ou envie uma resposta real no formulário
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground border-t pt-2">
                            💡 Para ver os logs: Menu → Execuções (ícone de relógio no canto inferior)
                          </div>
                        </AlertDescription>
                      </Alert>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Email:</span> {form.fields_mapping.email}
                  </div>
                  <div>
                    <span className="font-semibold">Nome:</span> {form.fields_mapping.name}
                  </div>
                  {form.fields_mapping.phone && (
                    <div>
                      <span className="font-semibold">Telefone:</span> {form.fields_mapping.phone}
                    </div>
                  )}
                  {form.fields_mapping.company && (
                    <div>
                      <span className="font-semibold">Empresa:</span> {form.fields_mapping.company}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
